import React, { useEffect, useState } from "react";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { useHistory } from "react-router-dom";
import { FiExternalLink } from "react-icons/fi";
import { toast } from "react-toastify";

import { getCheckAutonomo } from "screens/perfilAutonomo/api/getCheckAutonomo";
import Home from "../../assets/logo/home.png";
import Logo from "../../assets/logo/logo-large.png";
import Auth from "../../services/signIn";
import { msalInstance } from "../../utils/auth_azure";
import { UserState } from "../../interfaces";
import {
  BACKOFFICE_VIVAZ_DEFAULT_ROUTE,
  isBackofficeVivazAllowedPath,
} from "../../utils/backofficeVivazRoutes";
import * as Styles from "./styles";

/**
 * Tela de Login
 *
 * Responsável por:
 * 1. Mostrar interface de login
 * 2. Iniciar processo de autenticação MSAL
 * 3. Redirecionar para a tela correta após login bem-sucedido
 */
function Login() {
  // Hooks
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Redux state
  const {
    signed,
    isError,
    loading,
    permissoes,
    username,
    supervisorProduto,
    usuarioBackofficeVivaz,
  } = useSelector<RootStateOrAny, UserState>((state) => state.user);

  // Efeito para redirecionamento após login bem-sucedido
  useEffect(() => {
    if (signed) {
      try {
        // Verificar status de autônomo
        getCheckAutonomo(username);

        // Definir rota de redirecionamento
        let targetRoute = usuarioBackofficeVivaz
          ? BACKOFFICE_VIVAZ_DEFAULT_ROUTE
          : "/menu"; // Rota padrão

        // Verificar se há URL anterior salva
        const prevUrl = localStorage.getItem("@prevUrl");
        if (prevUrl) {
          // Limpar a URL anterior do storage
          localStorage.removeItem("@prevUrl");

          // Extrair o pathname se for URL completa
          const isAbsoluteUrl = prevUrl.startsWith("http");
          const prevPathname = isAbsoluteUrl
            ? new URL(prevUrl).pathname
            : prevUrl;
          targetRoute =
            usuarioBackofficeVivaz &&
            !isBackofficeVivazAllowedPath(prevPathname)
              ? BACKOFFICE_VIVAZ_DEFAULT_ROUTE
              : prevPathname;
        }
        // Se não houver URL anterior, verificar permissões
        else if (
          !usuarioBackofficeVivaz &&
          permissoes &&
          permissoes.length > 0
        ) {
          // Verificar se é recepcionista e não é supervisor
          const isReceptionist =
            permissoes.includes("tela_atendimento_visita") &&
            !supervisorProduto;
          targetRoute = isReceptionist ? "/plantao" : "/menu";
        }

        // Redirecionar para a rota adequada
        history.push(targetRoute);
      } catch (error) {
        console.error("[Login] Erro no redirecionamento:", error);
        // Em caso de erro, redireciona para o menu
        history.push(
          usuarioBackofficeVivaz ? BACKOFFICE_VIVAZ_DEFAULT_ROUTE : "/menu"
        );
      }
    }
  }, [
    signed,
    permissoes,
    username,
    history,
    supervisorProduto,
    usuarioBackofficeVivaz,
  ]);

  // Reset de erro
  useEffect(() => {
    if (isError) {
      dispatch({ type: "SET_IS_ERROR", payload: false });
    }
  }, [isError, dispatch]);

  // Iniciar processo de login
  const handleSignIn = async () => {
    try {
      setIsLoggingIn(true);
      await Auth(msalInstance);
    } catch (error) {
      console.error("[Login] Erro no login:", error);
      toast.error("Não foi possível realizar o login. Tente novamente.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Renderização do componente
  return (
    <Styles.Container>
      <Styles.ContainerImage>
        <Styles.Img src={Home} />
      </Styles.ContainerImage>

      <Styles.Form>
        <Styles.LogoSellit width={250} src={Logo} />
        <Styles.SizedBox style={{ marginTop: 60 }} />
        <Styles.Text>Facilite o seu dia e</Styles.Text>
        <Styles.Text>programe-se com mais</Styles.Text>
        <Styles.Text>agilidade.</Styles.Text>
        <Styles.SizedBox style={{ marginBottom: 80 }} />

        <Styles.BaseButtom
          onClick={handleSignIn}
          disabled={loading || isLoggingIn}
        >
          {loading || isLoggingIn ? (
            <Styles.Loading color="inherit" size={22} />
          ) : (
            "ENTRAR"
          )}
        </Styles.BaseButtom>

        <Styles.SizedBox style={{ marginTop: 20 }} />
        <a
          href="/termos-de-uso"
          style={{ textDecoration: "none" }}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Styles.TermosLink>
            <FiExternalLink
              color="#2D5BFF"
              size={16}
              style={{ marginRight: 8 }}
            />
            Termos de uso
          </Styles.TermosLink>
        </a>
      </Styles.Form>
    </Styles.Container>
  );
}

export default Login;
